import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useThemeStore } from '../store/themeStore';
import { notify } from '../utils/notify';
import { HiCog, HiUser, HiMoon, HiSun, HiShieldCheck, HiTrash, HiEye, HiEyeOff, HiPencil } from 'react-icons/hi';
import FuerzaContrasena from '../components/FuerzaContrasena';
import { medirContrasena } from '../utils/fuerzaContrasena';
import ConfirmModal from '../components/ConfirmModal';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../services/api';
import { mensajeDeError } from '../utils/apiError';


export default function Settings() {
    const { user, updateProfile, logout } = useAuthStore();
    const { isDark, toggleTheme } = useThemeStore();
    const navigate = useNavigate();
    const [fullName, setFullName] = useState(user?.full_name || '');
    // El nombre se lee, y se edita cuando lo pides.
    //
    // Un campo de texto abierto y un botón de guardar encendido dicen «esto
    // está a medias, termínalo». Lo que se viene a hacer casi siempre en esta
    // pantalla es mirar, no cambiar, así que por defecto se mira.
    const [editandoNombre, setEditandoNombre] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deletePassword, setDeletePassword] = useState('');
    const [deleting, setDeleting] = useState(false);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmarPassword, setConfirmarPassword] = useState('');
    // El formulario de contraseña empieza cerrado.
    //
    // Ajustes es una pantalla a la que se entra a cambiar el nombre o el tema, y
    // tres campos de contraseña siempre abiertos ocupan media pantalla para algo
    // que casi nunca se viene a hacer. Además, el gestor de contraseñas del
    // navegador tiende a rellenar lo que encuentra: un formulario que no existe
    // hasta que lo pides no se rellena solo.
    const [formularioAbierto, setFormularioAbierto] = useState(false);
    // Uno por campo. Un solo interruptor compartido obligaba a destapar los
    // tres a la vez para comprobar uno.
    const [verActual, setVerActual] = useState(false);
    const [verNueva, setVerNueva] = useState(false);
    const [verConfirmar, setVerConfirmar] = useState(false);
    const [changingPassword, setChangingPassword] = useState(false);
    const { t } = useTranslation();


    /**
     * El cursor entra en el campo al pulsar el lápiz.
     *
     * Aquí NO sirve `autoFocus`, y lo comprobé en el navegador antes de darlo
     * por bueno: `autoFocus` solo actúa cuando el elemento se monta, y este
     * campo no se monta al empezar a editar — ya estaba ahí, apagado, y lo
     * único que cambia es `disabled`. Medido: al pulsar el lápiz el foco se
     * quedaba en el botón.
     *
     * Con una referencia y un efecto sí, porque corre **después** de que React
     * haya quitado el `disabled`: un campo deshabilitado no acepta el foco, así
     * que el orden importa.
     */
    const campoNombre = useRef(null);

    useEffect(() => {
        if (editandoNombre) campoNombre.current?.focus();
    }, [editandoNombre]);

    /** Deja de editar y devuelve el campo a lo que hay guardado. */
    const cancelarNombre = () => {
        setFullName(user?.full_name || '');
        setEditandoNombre(false);
    };

    const handleSaveProfile = async (e) => {
        e.preventDefault();
        setSaving(true);
        const result = await updateProfile({ fullName });
        if (result.success) {
            notify.success(t('settings.profile_updated'));
            // Guardado: se vuelve a modo lectura. Quedarse en edición después
            // de acertar deja la duda de si llegó a guardarse.
            setEditandoNombre(false);
        } else {
            // Y si falló, NO se sale de edición: lo que escribiste sigue ahí
            // para corregirlo. Cerrar el campo tras un error tiraría el texto.
            notify.error(result.error);
        }
        setSaving(false);
    };

    const handleDeleteAccountClick = () => {
        setDeletePassword('');
        setShowDeleteConfirm(true);
    };

    const confirmDeleteAccount = async () => {
        setDeleting(true);
        try {
            await authAPI.deleteAccount(deletePassword || undefined);
            setShowDeleteConfirm(false);
            notify.success(t('settings.delete_account_success'));
            await logout();
            navigate('/');
        } catch (error) {
            notify.error(mensajeDeError(error, t('settings.delete_account_failed')));
        } finally {
            setDeleting(false);
        }
    };

    /**
     * Cierra el formulario y **borra lo escrito**.
     *
     * Lo segundo no es adorno. Sin ello, cancelar dejaría tu contraseña actual
     * guardada en el estado de la pantalla, lista para reaparecer al volver a
     * abrir el formulario. Cancelar significa que no pasó nada.
     */
    const cerrarFormulario = () => {
        setFormularioAbierto(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmarPassword('');
        setVerActual(false);
        setVerNueva(false);
        setVerConfirmar(false);
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();

        // Se comprueba aqui lo mismo que comprueba el servidor, para no gastar
        // una peticion en algo que ya sabemos que va a rechazar. Pero quien
        // decide es el servidor: esto es cortesia.
        if (newPassword !== confirmarPassword) {
            notify.error(t('settings.passwords_do_not_match'));
            return;
        }

        if (!medirContrasena(newPassword, { email: user?.email, nombre: user?.full_name }).valida) {
            notify.error(t('settings.password_not_strong_enough'));
            return;
        }

        setChangingPassword(true);
        try {
            const res = await authAPI.changePassword(currentPassword, newPassword);
            // Cambiar la contraseña cierra todas las sesiones abiertas, incluida
            // la de esta pestaña. El servidor devuelve un token nuevo para que
            // sigas dentro aquí y solo se cierren las demás.
            const nuevoToken = res.data?.data?.token;
            if (nuevoToken) useAuthStore.getState().setToken(nuevoToken);
            notify.success(t('settings.password_changed'));
            // Terminado: se recoge todo. Dejar los campos abiertos y vacíos
            // después de un cambio correcto invita a pensar que falta algo.
            cerrarFormulario();
        } catch (error) {
            notify.error(mensajeDeError(error, t('settings.password_change_failed')));
        } finally {
            setChangingPassword(false);
        }
    };

    return (
        <div className="max-w-2xl mx-auto space-y-8">
            {/* Header */}
            <div>
                <h1 className="page-title flex items-center gap-2">
                    <HiCog className="w-8 h-8 text-slate-500" />
                    {t('settings.title')}
                </h1>
                <p className="text-slate-500 dark:text-slate-400 mt-1">
                    {t('settings.subtitle')}
                </p>
            </div>

            {/* Profile Section */}
            <div className="card">
                <h2 className="section-title flex items-center gap-2 mb-6">
                    <HiUser className="w-5 h-5" />
                    {t('settings.profile')}
                </h2>

                <form onSubmit={handleSaveProfile} className="space-y-4">
                    <div className="flex items-center gap-4 mb-6">
                        <div className="w-16 h-16 bg-gradient-to-br from-primary-400 to-accent-400 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                            {user?.full_name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}
                        </div>
                        <div>
                            <p className="font-medium text-slate-900 dark:text-white">{user?.email}</p>
                            <p className="text-sm text-slate-500">
                                {user?.subscription_tier === 'pro' ? t('settings.pro_member') : t('settings.free_member')}
                            </p>
                        </div>
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-1.5">
                            <label htmlFor="fullName" className="label !mb-0">{t('settings.full_name')}</label>

                            {/* El lápiz ámbar de la regla 3, que es el color de
                                «editar» en toda la aplicación. Desaparece
                                mientras editas: entonces el camino son Guardar
                                y Cancelar, y un tercer control para entrar
                                donde ya estás no lleva a ningún sitio. */}
                            {!editandoNombre && (
                                <button
                                    type="button"
                                    onClick={() => setEditandoNombre(true)}
                                    className="icon-edit"
                                    title={t('settings.edit_name')}
                                    aria-label={t('settings.edit_name')}
                                >
                                    <HiPencil className="w-4 h-4" />
                                </button>
                            )}
                        </div>

                        <input
                            id="fullName"
                            ref={campoNombre}
                            type="text"
                            value={fullName}
                            onChange={(e) => setFullName(e.target.value)}
                            disabled={!editandoNombre}
                            // Mismo gris que el correo, que es el campo que
                            // nunca se puede tocar: así «no editable ahora» y
                            // «no editable nunca» se leen igual, que es lo que
                            // son mientras no pulses el lápiz.
                            className={`input ${editandoNombre ? '' : 'bg-slate-100 dark:bg-slate-700 cursor-not-allowed'}`}
                            placeholder={t('settings.full_name_placeholder')}
                        />
                    </div>

                    <div>
                        <label className="label">{t('settings.email')}</label>
                        <input
                            type="email"
                            value={user?.email || ''}
                            disabled
                            className="input bg-slate-100 dark:bg-slate-700 cursor-not-allowed"
                        />
                        <p className="text-xs text-slate-500 mt-1">{t('settings.email_cannot_change')}</p>
                    </div>

                    {/* Guardar se queda a la vista siempre, apagado mientras no
                        haya nada que guardar: esconderlo obligaría a descubrir
                        que existe. Cancelar solo aparece cuando hay algo que
                        cancelar. */}
                    <div className="flex gap-3">
                        <button
                            type="submit"
                            disabled={!editandoNombre || saving}
                            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {saving ? t('settings.saving') : t('settings.save_changes')}
                        </button>

                        {editandoNombre && (
                            <button
                                type="button"
                                onClick={cancelarNombre}
                                disabled={saving}
                                className="btn-secondary"
                            >
                                {t('common.cancel')}
                            </button>
                        )}
                    </div>
                </form>
            </div>

            {/* Appearance */}
            <div className="card">
                <h2 className="section-title flex items-center gap-2 mb-6">
                    {isDark ? <HiMoon className="w-5 h-5" /> : <HiSun className="w-5 h-5" />}
                    {t('settings.appearance')}
                </h2>

                <div className="flex items-center justify-between">
                    <div>
                        <p className="font-medium text-slate-900 dark:text-white">{t('settings.dark_mode')}</p>
                        <p className="text-sm text-slate-500">{t('settings.dark_mode_desc')}</p>
                    </div>
                    <button
                        onClick={toggleTheme}
                        className={`relative w-14 h-7 rounded-full transition-colors ${isDark ? 'bg-primary-500' : 'bg-slate-200'
                            }`}
                    >
                        <span
                            className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${isDark ? 'translate-x-7' : ''
                                }`}
                        />
                    </button>
                </div>
            </div>

            {/* Aquí había tres casillas —avisos de pedidos, alertas de productos y
                correos de marketing— que no hacían nada: sin `checked`, sin
                `onChange`, fuera de cualquier formulario y sin columna donde
                guardarse. Se podían desmarcar y volvían a salir marcadas al
                recargar.

                Y las tres prometían cosas que no existen: no se crea ningún
                aviso al recibir un pedido, la sincronización de existencias
                responde 501, y el único correo que manda la aplicación es el de
                restablecer la contraseña.

                La sección vuelve cuando haya algo que configurar de verdad, y
                con una regla: un aviso esencial —un cobro fallido— NO lleva
                interruptor. Apagarlo es perder el plan Pro sin enterarse.
                Ver el apartado K de docs/PENDIENTES.md. */}

            {/* Security */}
            <div className="card">
                <h2 className="section-title flex items-center gap-2 mb-6">
                    <HiShieldCheck className="w-5 h-5" />
                    {t('settings.security')}
                </h2>

                {user?.provider === 'google' ? (
                    <p className="text-sm text-slate-500">{t('settings.password_managed_by_google')}</p>
                ) : !formularioAbierto ? (
                    /* Cerrado: solo la puerta. Los tres campos aparecen al
                       pulsar, y se van con «Cancelar». */
                    <button
                        type="button"
                        onClick={() => setFormularioAbierto(true)}
                        className="btn-secondary w-full"
                    >
                        {t('settings.change_password')}
                    </button>
                ) : (
                    <form onSubmit={handleChangePassword} className="space-y-4">
                        <div>
                            <label htmlFor="currentPassword" className="block text-sm font-medium text-slate-600 dark:text-slate-300 mb-1.5">
                                {t('settings.current_password')}
                            </label>
                            <div className="relative">
                                <input
                                    id="currentPassword"
                                    type={verActual ? 'text' : 'password'}
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                    autoComplete="current-password"
                                    className="input pr-10"
                                    required
                                    // Acabas de pulsar para abrir esto: el
                                    // cursor ya está donde vas a escribir, sin
                                    // un clic más. Y para quien navega con el
                                    // teclado, es lo que evita tener que buscar
                                    // a tientas dónde quedó el foco.
                                    autoFocus
                                />
                                <button
                                    type="button"
                                    onClick={() => setVerActual(!verActual)}
                                    aria-label={t(verActual ? 'settings.hide_password' : 'settings.show_password')}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                >
                                    {verActual
                                        ? <HiEyeOff className="w-5 h-5 text-slate-400 hover:text-slate-500" />
                                        : <HiEye className="w-5 h-5 text-slate-400 hover:text-slate-500" />}
                                </button>
                            </div>
                        </div>

                        <div>
                            <label htmlFor="newPassword" className="block text-sm font-medium text-slate-600 dark:text-slate-300 mb-1.5">
                                {t('settings.new_password')}
                            </label>
                            <div className="relative">
                                <input
                                    id="newPassword"
                                    type={verNueva ? 'text' : 'password'}
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    autoComplete="new-password"
                                    className="input pr-10"
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setVerNueva(!verNueva)}
                                    aria-label={t(verNueva ? 'settings.hide_password' : 'settings.show_password')}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                >
                                    {verNueva
                                        ? <HiEyeOff className="w-5 h-5 text-slate-400 hover:text-slate-500" />
                                        : <HiEye className="w-5 h-5 text-slate-400 hover:text-slate-500" />}
                                </button>
                            </div>
                            <FuerzaContrasena
                                contrasena={newPassword}
                                email={user?.email}
                                nombre={user?.full_name}
                            />
                        </div>

                        <div>
                            <label htmlFor="confirmarPassword" className="block text-sm font-medium text-slate-600 dark:text-slate-300 mb-1.5">
                                {t('settings.confirm_new_password')}
                            </label>
                            <div className="relative">
                                <input
                                    id="confirmarPassword"
                                    type={verConfirmar ? 'text' : 'password'}
                                    value={confirmarPassword}
                                    onChange={(e) => setConfirmarPassword(e.target.value)}
                                    autoComplete="new-password"
                                    className="input pr-10"
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setVerConfirmar(!verConfirmar)}
                                    aria-label={t(verConfirmar ? 'settings.hide_password' : 'settings.show_password')}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                >
                                    {verConfirmar
                                        ? <HiEyeOff className="w-5 h-5 text-slate-400 hover:text-slate-500" />
                                        : <HiEye className="w-5 h-5 text-slate-400 hover:text-slate-500" />}
                                </button>
                            </div>
                            {/* Sin este campo, una errata se convertiía en tu
                                contraseña nueva y no habia forma de saberlo. */}
                            {confirmarPassword && (
                                <p className={`mt-1.5 text-xs ${
                                    confirmarPassword === newPassword ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'
                                }`}>
                                    {t(confirmarPassword === newPassword ? 'password_strength.match' : 'password_strength.no_match')}
                                </p>
                            )}
                        </div>

                        <p className="text-xs text-slate-500 dark:text-slate-400">
                            {t('settings.password_change_closes_sessions')}
                        </p>

                        {/* «Aceptar» es el que manda el cambio; «Cancelar»
                            recoge los campos sin tocar nada. Cancelar va en
                            segundo lugar y sin color de acción: es la salida,
                            no lo que se viene a hacer aquí.

                            `type="button"` en Cancelar a propósito — dentro de
                            un <form>, un botón sin tipo es `submit`, así que
                            cancelar habría intentado cambiar la contraseña. */}
                        <div className="flex gap-3">
                            <button type="submit" disabled={changingPassword} className="btn-primary flex-1">
                                {changingPassword ? t('settings.saving') : t('common.accept')}
                            </button>
                            <button
                                type="button"
                                onClick={cerrarFormulario}
                                disabled={changingPassword}
                                className="btn-secondary flex-1"
                            >
                                {t('common.cancel')}
                            </button>
                        </div>
                    </form>
                )}
            </div>

            {/* Danger Zone */}
            <div className="card border-red-200 dark:border-red-900/50">
                <h2 className="section-title flex items-center gap-2 mb-6 text-red-500">
                    <HiTrash className="w-5 h-5" />
                    {t('settings.danger_zone')}
                </h2>

                <p className="text-sm text-slate-500 mb-4">
                    {t('settings.danger_zone_desc')}
                </p>

                <button onClick={handleDeleteAccountClick} className="btn-danger">
                    {t('settings.delete_account')}
                </button>
            </div>

            {/* Logout */}
            <button
                onClick={logout}
                className="btn-secondary w-full"
            >
                {t('dashboard.sign_out')}
            </button>

            <ConfirmModal
                isOpen={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                onConfirm={confirmDeleteAccount}
                title={t('common.confirm_title', 'Confirmar acción')}
                message={t('settings.delete_account_confirm', '¿Estás seguro de que quieres eliminar tu cuenta permanentemente?')}
                confirmText={t('common.delete', 'Eliminar')}
                isDanger={true}
                isLoading={deleting}
            >
                {user?.provider !== 'google' && (
                    <div>
                        <label htmlFor="deletePassword" className="block text-sm font-medium text-slate-600 dark:text-slate-300 mb-1.5">
                            {t('settings.confirm_with_password')}
                        </label>
                        <input
                            id="deletePassword"
                            type="password"
                            value={deletePassword}
                            onChange={(e) => setDeletePassword(e.target.value)}
                            className="input"
                            autoComplete="current-password"
                        />
                    </div>
                )}
            </ConfirmModal>
        </div>
    );
}
